import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Primitives";
import { ARTICLES, INSIGHT_CATEGORIES } from "../data/insights";

export const DraftBadge = () => (
  <span className="inline-flex items-center rounded-sm border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-800">Sample draft</span>
);

const ArticleCard = ({ a }) => (
  <Link to={`/insights/${a.slug}`} className="group flex h-full flex-col" data-testid={`insight-card-${a.slug}`}>
    <div className="aspect-[16/10] overflow-hidden rounded-lg bg-slate-100">
      <img src={a.image} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
    </div>
    <div className="mt-6 flex items-center gap-3"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2563EB]">{a.category}</span><DraftBadge /></div>
    <h2 className="mt-4 font-display text-xl font-semibold leading-snug tracking-tight text-[#0B1220] transition-colors group-hover:text-[#2563EB]">{a.title}</h2>
    <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{a.excerpt}</p>
    <span className="mt-auto flex items-center gap-2 pt-6 text-sm font-medium text-slate-500">{a.readTime} <ArrowUpRight className="h-4 w-4" /></span>
  </Link>
);

export default function Insights() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ARTICLES.filter((a) => (category === "All" || a.category === category) && (!q || `${a.title} ${a.excerpt} ${a.category}`.toLowerCase().includes(q)));
  }, [query, category]);

  return (
    <>
      <Seo title="Insights" description="Perspectives on audit and assurance, technology risk, business transformation, financial advisory, tax, legal and independent professional services." path="/insights" />
      <section className="border-b border-slate-200 bg-[#F4F6F8]">
        <div className="container-x py-20 lg:py-24">
          <Reveal>
            <p className="eyebrow">Insights</p>
            <h1 className="h-display mt-6 max-w-4xl">Perspectives for leaders navigating complexity.</h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500" data-testid="insights-draft-notice">The articles below are clearly labelled sample drafts for layout demonstration. They are general in nature, contain no verified research or statistics, and do not constitute professional advice.</p>
          </Reveal>
          <div className="mt-12 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <label htmlFor="insight-search" className="sr-only">Search insights</label>
              <input id="insight-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search insights" className="h-12 w-full rounded-md border border-slate-300 bg-white pl-11 pr-4 text-[15px] focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]" data-testid="insights-search-input" />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
            {["All", ...INSIGHT_CATEGORIES].map((c) => (
              <button key={c} type="button" onClick={() => setCategory(c)} aria-pressed={category === c}
                className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${category === c ? "border-[#0B1220] bg-[#0B1220] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-[#0B1220] hover:text-[#0B1220]"}`}
                data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white py-20 lg:py-24">
        <div className="container-x">
          <p className="text-sm text-slate-500" data-testid="insights-result-count">{results.length} {results.length === 1 ? "article" : "articles"}</p>
          {results.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-slate-300 px-8 py-16 text-center" data-testid="insights-empty-state">
              <p className="font-display text-lg font-semibold text-[#0B1220]">No articles match your search.</p>
              <button type="button" onClick={() => { setQuery(""); setCategory("All"); }} className="mt-4 text-sm font-semibold text-[#2563EB]" data-testid="insights-reset-button">Clear filters</button>
            </div>
          ) : (
            <div className="mt-10 grid gap-x-10 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
              {results.map((a) => <ArticleCard key={a.slug} a={a} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
